// Keputusan murni Custom Chart. Cuma boleh import moment + path relatif,
// biar bisa diuji pakai node.
import moment from 'moment';
import { suggestInterval, estimatePointCount } from './rangeDisplay.js';
import {
  findPoint, channelModeOf, channelOfRow, valueOfRow,
} from './customChartCatalog.js';

export const MAX_SERIES = 8;
export const MAX_UNITS = 4;
export const MAX_NAME = 80;
export const MAX_POINTS = 10000;
export const WARN_POINTS = 1500;
export const PALETTE = ['#0084ff', '#F97316', '#10B981', '#884DFF', '#EF4444', '#EAB308', '#06B6D4', '#EC4899'];

export const INTERVAL_OPTIONS = [
  { value: 'MINUTE', seconds: 60 },
  { value: 'HOUR', seconds: 3600 },
  { value: 'DAY', seconds: 86400 },
];
export const INTERVAL_TEXT = { minute: 'By minute', hour: 'Hourly', day: 'Daily' };

export const RANGE_PRESETS = [
  { value: 'last6h', label: 'Last 6 hours' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7d', label: 'Last 7 days' },
  { value: 'last30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
];

export const PERIOD_KINDS = [
  { value: 'same', label: 'Same as range' },
  { value: 'previous', label: 'Previous period' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'from', label: 'Starting from…' },
];

const PRESET_HOURS = { last6h: 6, last7d: 24 * 7, last30d: 24 * 30 };
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const HTML_ESCAPE = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};

export function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => HTML_ESCAPE[c]);
}

export function resolveMainRange(range, nowMs) {
  const now = moment(nowMs);
  const preset = range && range.preset;
  if (preset === 'custom') return { startMs: Number(range.startMs), endMs: Number(range.endMs) };
  if (preset === 'today') return { startMs: now.clone().startOf('day').valueOf(), endMs: now.valueOf() };
  if (preset === 'yesterday') {
    const day = now.clone().subtract(1, 'day');
    return { startMs: day.clone().startOf('day').valueOf(), endMs: day.clone().endOf('day').valueOf() };
  }
  const hours = PRESET_HOURS[preset] || PRESET_HOURS.last6h;
  return { startMs: now.clone().subtract(hours, 'hours').valueOf(), endMs: now.valueOf() };
}

export function resolveInterval(interval, mainRange) {
  if (['minute', 'hour', 'day'].includes(interval)) return interval;
  const hit = suggestInterval(mainRange.startMs, mainRange.endMs, INTERVAL_OPTIONS);
  return hit ? hit.toLowerCase() : 'hour';
}

export function estimatePoints(mainRange, interval) {
  const option = INTERVAL_OPTIONS.find((o) => o.value === String(interval).toUpperCase());
  return option ? estimatePointCount(mainRange.startMs, mainRange.endMs, option.seconds) : 0;
}

export function resolveSeriesPeriod(period, mainRange) {
  const { startMs, endMs } = mainRange;
  const length = endMs - startMs;
  const kind = period && period.kind;
  let seriesStart = startMs;
  if (kind === 'previous') seriesStart = startMs - length;
  else if (kind === 'lastWeek') seriesStart = moment(startMs).subtract(7, 'days').valueOf();
  else if (kind === 'lastMonth') seriesStart = moment(startMs).subtract(1, 'month').valueOf();
  else if (kind === 'from' && Number.isFinite(Number(period.startMs))) seriesStart = Number(period.startMs);
  return { startMs: seriesStart, endMs: seriesStart + length, shiftMs: startMs - seriesStart };
}

export function periodLabel(period) {
  const kind = period && period.kind;
  if (kind === 'previous') return 'Previous period';
  if (kind === 'lastWeek') return 'Last week';
  if (kind === 'lastMonth') return 'Last month';
  if (kind === 'from') return `From ${moment(Number(period.startMs)).format('DD MMM YYYY')}`;
  return '';
}

export function seriesLabel(series, assetName) {
  const entry = findPoint(series.deviceType, series.point);
  const pointText = `${entry ? entry.label : series.point}${series.channel ? ` ${series.channel}` : ''}`;
  const parts = [assetName || 'Unknown vessel', pointText];
  const period = periodLabel(series.period);
  if (period) parts.push(period);
  return parts.join(' · ');
}

function periodKey(period) {
  const kind = (period && period.kind) || 'same';
  return kind === 'from' ? `from:${period.startMs}` : kind;
}

export function seriesKey(series) {
  return [series.massId, series.deviceUid, series.point, series.channel || '', periodKey(series.period)].join('|');
}

export function unitsOf(seriesList) {
  const units = [];
  (seriesList || []).forEach((s) => {
    const entry = findPoint(s.deviceType, s.point);
    if (entry && !units.includes(entry.unit)) units.push(entry.unit);
  });
  return units;
}

function isKnownDevice(deviceNameByUid, uid) {
  return !deviceNameByUid || Object.prototype.hasOwnProperty.call(deviceNameByUid, uid);
}

// `nowMs` & `deviceNameByUid` opsional: tanpa itu, cek masa depan / batas titik / device dilewati.
export function validateChart(chart, { nowMs, deviceNameByUid } = {}) {
  const errors = [];
  const name = String((chart && chart.name) || '').trim();
  if (!name) errors.push('Chart name is required.');
  else if (name.length > MAX_NAME) errors.push(`Chart name must be at most ${MAX_NAME} characters.`);
  const range = chart && chart.range;
  const customInvalid = range && range.preset === 'custom' && !(Number(range.endMs) > Number(range.startMs));
  if (customInvalid) errors.push('Custom range end must be after its start.');
  const series = (chart && chart.series) || [];
  if (series.length === 0) errors.push('Add at least one series.');
  if (series.length > MAX_SERIES) errors.push(`A chart can have at most ${MAX_SERIES} series.`);
  const seen = new Set();
  series.forEach((s, i) => {
    const label = `Series ${i + 1}`;
    if (!s.massId || !s.deviceUid || !findPoint(s.deviceType, s.point)) {
      errors.push(`${label} is incomplete.`);
      return;
    }
    if (!isKnownDevice(deviceNameByUid, s.deviceUid)) {
      errors.push(`${label}: vessel or device is no longer available.`);
      return;
    }
    if (channelModeOf(s.deviceType) && !s.channel) {
      errors.push(`${label} needs a channel.`);
      return;
    }
    if (s.period && s.period.kind === 'from') {
      const start = Number(s.period.startMs);
      if (!Number.isFinite(start)) {
        errors.push(`${label} needs a start date.`);
        return;
      }
      if (Number.isFinite(nowMs) && start > nowMs) {
        errors.push(`${label} start date is in the future.`);
        return;
      }
    }
    if (!HEX_COLOR.test(s.color || '')) {
      errors.push(`${label} has an invalid color.`);
      return;
    }
    const key = seriesKey(s);
    if (seen.has(key)) errors.push(`${label} duplicates another series.`);
    seen.add(key);
  });
  if (unitsOf(series).length > MAX_UNITS) errors.push(`A chart can use at most ${MAX_UNITS} units.`);
  if (Number.isFinite(nowMs) && !customInvalid) {
    const main = resolveMainRange(range, nowMs);
    const points = estimatePoints(main, resolveInterval(chart && chart.interval, main));
    if (points > MAX_POINTS) {
      errors.push(`Too many points (~${points.toLocaleString('en-US')} per series). Choose a coarser interval or a shorter range.`);
    }
  }
  return errors;
}

export function createSeries(existingSeries) {
  const list = existingSeries || [];
  const maxNo = list.reduce((acc, s) => {
    const match = /^s(\d+)$/.exec((s && s.id) || '');
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  const used = new Set(list.map((s) => s && s.color));
  return {
    id: `s${maxNo + 1}`,
    massId: null,
    deviceUid: '',
    deviceType: '',
    point: '',
    channel: null,
    period: { kind: 'same' },
    color: PALETTE.find((c) => !used.has(c)) || PALETTE[list.length % PALETTE.length],
  };
}

export function createChart({ companyId, order, nowMs }) {
  return {
    id: nowMs,
    version: 2,
    companyId,
    name: '',
    range: { preset: 'last6h' },
    interval: 'auto',
    width: 'full',
    order,
    series: [createSeries([])],
  };
}

const INTERVAL_MS = { minute: 60000, hour: 3600000, day: 86400000 };

// Start utama & start seri dibulatkan ke awal interval, pergeseran = selisihnya,
// biar bucket seri jatuh di timestamp yang sama dengan bucket rentang utama.
export function requestFor(series, mainRange, interval, nowMs) {
  const unit = INTERVAL_MS[interval] ? interval : 'hour';
  const step = INTERVAL_MS[unit];
  const alignedStart = moment(mainRange.startMs).startOf(unit).valueOf();
  const seriesStart = moment(resolveSeriesPeriod(series.period, mainRange).startMs).startOf(unit).valueOf();
  const shiftMs = Math.round((alignedStart - seriesStart) / step) * step;
  const startSec = Math.floor((alignedStart - shiftMs) / 1000);
  let endSec = moment(mainRange.endMs - shiftMs).endOf('minute').unix();
  if (Number.isFinite(nowMs)) endSec = Math.min(endSec, Math.floor(nowMs / 1000));
  return {
    key: `${series.deviceType}|${series.deviceUid}|${startSec}|${endSec}`,
    deviceType: series.deviceType,
    deviceUid: series.deviceUid,
    startSec,
    endSec,
    shiftMs,
  };
}

export function buildFetchPlan(chart, mainRange, interval, { nowMs, deviceNameByUid } = {}) {
  const plan = [];
  const seen = new Set();
  ((chart && chart.series) || []).forEach((s) => {
    if (!s.deviceUid || !s.deviceType || !isKnownDevice(deviceNameByUid, s.deviceUid)) return;
    const req = requestFor(s, mainRange, interval, nowMs);
    if (req.startSec >= req.endSec || seen.has(req.key)) return;
    seen.add(req.key);
    plan.push({
      key: req.key, deviceType: req.deviceType, deviceUid: req.deviceUid, startSec: req.startSec, endSec: req.endSec,
    });
  });
  return plan;
}

function countNames(names) {
  return names.reduce((acc, name) => ({ ...acc, [name]: (acc[name] || 0) + 1 }), {});
}

// Legend, CSV, dan tooltip pakai nama sebagai kunci, jadi wajib unik.
function uniqueNames(labels, series, deviceNameByUid) {
  const first = countNames(labels);
  const withDevice = labels.map((name, i) => {
    if (first[name] < 2) return name;
    const uid = series[i].deviceUid;
    return `${name} · ${(deviceNameByUid && deviceNameByUid[uid]) || uid}`;
  });
  const second = countNames(withDevice);
  const seen = {};
  return withDevice.map((name) => {
    if (second[name] < 2) return name;
    seen[name] = (seen[name] || 0) + 1;
    return seen[name] === 1 ? name : `${name} (${seen[name]})`;
  });
}

export function buildDatasets({
  chart, rowsByKey = {}, statusByKey = {}, mainRange, interval, nowMs, assetNameById = {}, deviceNameByUid,
}) {
  const series = (chart && chart.series) || [];
  const stepSec = (INTERVAL_MS[interval] || INTERVAL_MS.hour) / 1000;
  const perSeries = series.map((s) => {
    const req = requestFor(s, mainRange, interval, nowMs);
    const status = isKnownDevice(deviceNameByUid, s.deviceUid) ? statusByKey[req.key] : 'missing';
    const values = new Map();
    if (status !== 'missing' && status !== 'failed' && status !== 'truncated') {
      const mode = channelModeOf(s.deviceType);
      (rowsByKey[req.key] || []).forEach((row) => {
        if (!row || row.timestamp === undefined) return;
        if (row.deviceId !== undefined && row.deviceId !== s.deviceUid) return;
        if (mode && channelOfRow(s.deviceType, row) !== s.channel) return;
        const value = valueOfRow(s.deviceType, s.point, row);
        if (value === null) return;
        // Timestamp baris = waktu sampel, bukan awal bucket; dibulatkan ke bucket biar seri sejajar.
        const bucketSec = req.startSec + Math.floor((Number(row.timestamp) - req.startSec) / stepSec) * stepSec;
        values.set(bucketSec * 1000 + req.shiftMs, value);
      });
    }
    return {
      s, req, status, values,
    };
  });

  const all = new Set();
  perSeries.forEach((p) => p.values.forEach((_, ts) => all.add(ts)));
  const timestamps = Array.from(all).sort((a, b) => a - b);
  const names = uniqueNames(series.map((s) => seriesLabel(s, assetNameById[s.massId])), series, deviceNameByUid);

  const datasets = perSeries.map(({
    s, req, status, values,
  }, i) => {
    const entry = findPoint(s.deviceType, s.point);
    let finalStatus = ['missing', 'failed', 'truncated'].includes(status) ? status : 'ok';
    if (finalStatus === 'ok' && values.size === 0) finalStatus = 'empty';
    return {
      id: s.id,
      name: names[i],
      axis: entry ? entry.unit : '',
      unit: entry ? entry.unit : '',
      color: s.color,
      shiftMs: req.shiftMs,
      data: timestamps.map((ts) => (values.has(ts) ? values.get(ts) : null)),
      realTs: timestamps.map((ts) => ts - req.shiftMs),
      status: finalStatus,
    };
  });

  return { timestamps, datasets };
}

// Chart lama gak nyimpen company; pemiliknya dibaca dari aset yang disalin di dalamnya.
export function legacyBelongsTo(old, companyId) {
  if (companyId === null || companyId === undefined || companyId === '') return false;
  const owner = old && old.asset ? old.asset.massCompId : undefined;
  return owner !== undefined && owner !== null && String(owner) === String(companyId);
}

const LEGACY_POSITION = { port: 'PORT', starboard: 'STARBOARD' };

function legacyPoint(deviceType, dataPoint) {
  if (deviceType === 'rpm' || deviceType === 'temperature') {
    return { point: deviceType, channel: LEGACY_POSITION[String(dataPoint).toLowerCase()] || null };
  }
  if (deviceType === 'ae') {
    const match = /^ae\s*(\d+)$/i.exec(String(dataPoint));
    return { point: 'rpm', channel: match ? `AE${match[1]}` : null };
  }
  return { point: dataPoint, channel: null };
}

function toMs(value) {
  if (value === null || value === undefined) return null;
  const m = moment(value);
  return m.isValid() ? m.valueOf() : null;
}

// Chart format lama (ChartDB) -> format v2. Data point yang udah dibuang dilewati.
export function migrateLegacyChart(old, companyId) {
  const source = old || {};
  const massId = source.asset ? source.asset.massId : null;
  const series = [];
  const units = [];
  (source.devicesData || []).forEach((device) => {
    (device.selectedDataPoints || []).forEach((dataPoint) => {
      const { point, channel } = legacyPoint(device.devcType, dataPoint);
      const entry = findPoint(device.devcType, point);
      if (!entry || (channelModeOf(device.devcType) && !channel)) return;
      if (series.length >= MAX_SERIES) return;
      if (!units.includes(entry.unit)) {
        if (units.length >= MAX_UNITS) return;
        units.push(entry.unit);
      }
      series.push({
        id: `s${series.length + 1}`,
        massId,
        deviceUid: device.devcUniqueId,
        deviceType: device.devcType,
        point,
        channel,
        period: { kind: 'same' },
        color: PALETTE[series.length % PALETTE.length],
      });
    });
  });
  const start = toMs(source.dateRange && source.dateRange.start);
  const end = toMs(source.dateRange && source.dateRange.end);
  return {
    id: source.id,
    version: 2,
    companyId,
    name: source.asset && source.asset.massName ? `${source.asset.massName} chart` : 'Chart',
    range: start !== null && end !== null && end > start
      ? { preset: 'custom', startMs: start, endMs: end }
      : { preset: 'last6h' },
    interval: 'auto',
    width: source.width === 'half' ? 'half' : 'full',
    order: Number.isFinite(Number(source.order)) ? Number(source.order) : 0,
    series,
  };
}
